import { useEffect, useMemo, useState } from 'react';
import { useUIStore } from '../store';
import { identityApi } from '../api/client';
import {
  BRANDING_STATUS,
  brandAccentCssVars,
  fallbackBrandingState,
  isManagedLogoReference,
} from './brandingPresentation';
import {
  BrandingContext,
  currentPortalIsMain,
  currentPortalKey,
  getCachedPortalBranding,
  loadPortalBranding,
  subscribeToPortalBrandingInvalidation,
} from './portalBrandingState';

// ─── Portal Branding Provider ──────────────────────────────────────────────────
// Mounts once at the app root and publishes the shared branding state.
//
// Deliberately plain React context rather than React Query: branding must not
// live in the auth-scoped query cache that `SessionQueryBoundary` clears on
// identity/token change, and must never become a shared source of stale Operator
// or customer data.

const BRAND_VAR_NAMES = ['--brand-accent', '--brand-accent-hover', '--brand-accent-contrast'];

export function BrandingProvider({ children }) {
  // The origin cannot change without a full document load, so this is read once.
  const [portalKey] = useState(currentPortalKey);
  // Presentation-only hostname classification, read once alongside the origin.
  // It decides nothing but whether AlgoSys's own marketing content is eligible.
  const [isMainPortal] = useState(currentPortalIsMain);
  const [branding, setBranding] = useState(
    () => getCachedPortalBranding(portalKey) ?? fallbackBrandingState(BRANDING_STATUS.LOADING)
  );
  // The loaded custom logo as `{ reference, url }`, or null to use the bundled
  // AlgoSys mark. The URL is tied to the managed reference that produced it.
  const [loadedLogo, setLoadedLogo] = useState(null);
  const theme = useUIStore((s) => s.theme);

  // When the Operator changes their own logo, the settings page invalidates the
  // current portal's cached branding; re-resolve it live, without a reload. The
  // key check keeps one origin's refresh from touching a different origin.
  const [revision, setRevision] = useState(0);
  useEffect(
    () => subscribeToPortalBrandingInvalidation((invalidatedKey) => {
      if (invalidatedKey === portalKey) setRevision((r) => r + 1);
    }),
    [portalKey]
  );

  useEffect(() => {
    // `active` plus the origin re-check stop a slow, superseded or post-unmount
    // response from replacing branding that has already moved on.
    let active = true;

    loadPortalBranding(portalKey, { isMainPortal }).then(
      (resolved) => {
        if (active && currentPortalKey() === portalKey) setBranding(resolved);
      },
      () => {
        // Unknown, unavailable or malformed: keep the fixed AlgoSys visual
        // defaults but stay `unresolved`, so AlgoSys-specific marketing, contact
        // details and company attribution remain suppressed. Never fall back to
        // a previously resolved Operator's branding, and never surface the backend
        // error or any Operator identifier in the UI.
        if (active && currentPortalKey() === portalKey) {
          setBranding(fallbackBrandingState(BRANDING_STATUS.UNRESOLVED));
        }
      }
    );

    return () => {
      active = false;
    };
  }, [portalKey, isMainPortal, revision]);

  // Fetch the current portal's custom logo only when the validated branding
  // state identifies a branded portal holding a managed logo reference. The
  // public /auth/portal-logo endpoint is the only image source — the raw
  // reference is never turned into a URL — and the request goes through the
  // interceptor-free public client, so no bearer token is attached and a
  // failure can never clear a session. Any failure keeps the bundled AlgoSys
  // logo.
  //
  // Keyed on the validated reference. Each run owns at most one object URL and
  // its cleanup revokes it, on replacement, removal and unmount alike.
  const logoReference = branding?.status === BRANDING_STATUS.BRANDED
    && isManagedLogoReference(branding?.logoReference)
    ? branding.logoReference
    : null;

  useEffect(() => {
    if (!logoReference) return undefined;
    let cancelled = false;
    let objectUrl = null;

    identityApi.portalLogo().then(
      (blob) => {
        // A superseded or post-unmount response must never restore a logo.
        if (cancelled) return;
        // Only a non-empty PNG Blob may become an image source.
        if (!(blob instanceof Blob) || blob.size === 0) return;
        if (blob.type !== 'image/png' && blob.type !== '') return;
        objectUrl = URL.createObjectURL(blob);
        setLoadedLogo({ reference: logoReference, url: objectUrl });
      },
      () => {
        // Keep the bundled AlgoSys logo; the portal is unaffected.
      }
    );

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        // Drop the revoked URL so a later return to this same reference can
        // never match it.
        const revoked = objectUrl;
        setLoadedLogo((prev) => (prev?.url === revoked ? null : prev));
      }
    };
  }, [logoReference]);

  // Passive effects run after the render that commits a new reference, so the
  // match is checked during render: a URL loaded for another reference (or for
  // a portal that no longer has one) is never exposed, not even for one frame.
  const logoSrc = logoReference && loadedLogo?.reference === logoReference
    ? loadedLogo.url
    : null;

  // Apply the Operator accent as CSS custom properties. The cleanup removes them
  // on unmount and before every re-apply, so switching back to the default brand
  // or changing theme restores the stylesheet values and no variable leaks
  // between portals.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const root = document.documentElement;
    const vars = brandAccentCssVars(branding, theme);
    if (!vars) {
      BRAND_VAR_NAMES.forEach((name) => root.style.removeProperty(name));
      return undefined;
    }
    Object.entries(vars).forEach(([name, value]) => {
      if (value) root.style.setProperty(name, value);
    });
    return () => {
      BRAND_VAR_NAMES.forEach((name) => root.style.removeProperty(name));
    };
  }, [branding, theme]);

  // Children render immediately with the defaults: a decorative branding request
  // must never block the authentication system or show a full-page loader.
  const value = useMemo(
    () => (logoSrc ? { ...branding, logoSrc } : branding),
    [branding, logoSrc]
  );
  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}
